import React, { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SwaggerUI from "swagger-ui-react";
import "swagger-ui-react/swagger-ui.css";
import { useAuth } from "../auth/AuthContext";
import { useOpenApiSpec } from "../hooks/useOpenApiSpec";
import { useI18n } from "../i18n/I18nContext";
import ContractOverview from "./ContractOverview";
import DocsLayout from "./DocsLayout";

const isSandboxExpiredResponse = (response) => {
    if (![401, 403].includes(response?.status)) return false;

    const body = response.obj || response.data || response.body || response.text || "";
    if (body?.code === "SANDBOX_ACCESS_EXPIRED") return true;

    return typeof body === "string" && body.includes("SANDBOX_ACCESS_EXPIRED");
};

const ProtectedSwagger = ({
    attachBearerToken = true,
    authLabel,
    authStatusLabel,
    extraActions = null,
    service,
    spec: providedSpec,
    url,
}) => {
    const { getAccessToken, signOut } = useAuth();
    const { language, t } = useI18n();
    const { error: specError, loading: specLoading, spec } = useOpenApiSpec(url, providedSpec, service);
    const [requestError, setRequestError] = useState("");
    const signOutTimerRef = useRef(null);

    const scheduleSignOut = useCallback(() => {
        if (signOutTimerRef.current) return;

        signOutTimerRef.current = window.setTimeout(() => {
            signOut();
        }, 1800);
    }, [signOut]);

    const requestInterceptor = useMemo(
        () => async (request) => {
            if (!attachBearerToken) return request;

            try {
                const token = await getAccessToken();
                request.headers = request.headers || {};
                request.headers.Authorization = `Bearer ${token}`;
                setRequestError("");
                return request;
            } catch (error) {
                setRequestError(
                    error.message || t("auth.sessionValidationFailed")
                );
                scheduleSignOut();
                return request;
            }
        },
        [attachBearerToken, getAccessToken, scheduleSignOut, t]
    );

    const responseInterceptor = useMemo(
        () => (response) => {
            if (isSandboxExpiredResponse(response)) {
                setRequestError(
                    t("auth.sandboxExpired")
                );
                scheduleSignOut();
            }

            return response;
        },
        [scheduleSignOut, t]
    );

    const swaggerContent = specLoading ? (
        <section className="contract-loading">
            <span className="auth-spinner" aria-hidden="true" />
            <p>{t("contract.loading")}</p>
        </section>
    ) : spec ? (
        <SwaggerUI
            key={`${url || service?.key || "provided"}-${language}`}
            spec={spec}
            requestInterceptor={attachBearerToken ? requestInterceptor : undefined}
            responseInterceptor={responseInterceptor}
            persistAuthorization
            docExpansion="list"
        />
    ) : null;

    const actions = (
        <>
            <span className="auth-badge">{authLabel || t("auth.cognito")}</span>
            <span className="auth-badge auth-badge-success">
                {authStatusLabel || (attachBearerToken ? "Bearer token" : "Access token")}
            </span>
            {extraActions}
        </>
    );

    return (
        <DocsLayout
            actions={actions}
            eyebrow={t("contract.openApiContract")}
            subtitle={service ? t(`services.${service.key}.description`) : ""}
            title={service ? t(`services.${service.key}.name`) : "Swagger"}
        >
            <div className="swagger-toolbar">
                <Link to="/">{t("contract.return")}</Link>
                {service ? (
                    <div className="swagger-summary">
                        <span>{service.method}</span>
                        <code>{service.endpoint}</code>
                        <strong>{service.version}</strong>
                    </div>
                ) : null}
            </div>
            <ContractOverview error={specError} loading={specLoading} service={service} spec={spec} />
            <section className="swagger-panel">
                <div className="contract-section-title">
                    <h3>{t("contract.interactiveSwagger")}</h3>
                    <span>{t("contract.tryItOut")}</span>
                </div>
                {requestError ? <div className="swagger-error">{requestError}</div> : null}
                {specError ? <div className="swagger-error">{specError}</div> : null}
                {swaggerContent}
            </section>
        </DocsLayout>
    );
};

export default ProtectedSwagger;
